import * as THREE from "three";
import XmdSaver from "./XmdSaver";
import debug from "debug";

const log = debug("app:xmd");
const CACHE_KEY = "xmdVaultCache";

/**
 * Class for tracking changes in the scene and managing XMD data.
 * @category Components
 * @subcategory XmdManager
 */
export default class XmdTracker {
  /**
   * Constructs the XmdTracker instance.
   * @param {Object} experience - The experience object containing scene, eventHub, and other context.
   */
  constructor(experience) {
    this.experience = experience;
    this.scene = this.experience.scene;
    this.eventHub = this.experience.eventHub;
    this.xmdAlreadyLoaded = false;

    // Setup event listeners
    this.eventHub.on("exportDesign", () => {
      log("export design initiated");
      const currentSceneInfo = this.captureSceneInfo();
      XmdSaver.saveAsXmd(currentSceneInfo);
    });

    this.eventHub.on("xmd", () => {
      this.handleSceneChange();
    });

    this.eventHub.on("xmd-loaded", () => {
      this.xmdAlreadyLoaded = true;
      log("✅ XMD loaded — tracker now allowed to save scene state");
      // start watching for project name edits when UI is ready
      this.attachProjectNameWatcher();
      
      // After loading is complete, capture the loaded scene state to window.makeJson
      // Small delay to ensure all parts are fully loaded and rendered
      setTimeout(() => {
        log("📸 Capturing loaded scene state to window.makeJson");
        this.updateMakeJson();
      }, 500);
    });

    // Listen for new project creation to clear cache and UUIDs
    this.eventHub.on("newProject", (data) => {
      log("🆕 New project created, clearing XMD cache and UUIDs");
      
      // Clear sessionStorage cache
      sessionStorage.removeItem(CACHE_KEY);
      
      // Clear in-memory UUIDs
      this.experience.projectUuid = null;
      window.currentProjectUuid = null;
      
      // Allow tracker to save new state (generate fresh UUID on next save)
      this.xmdAlreadyLoaded = true;
      
      log("✅ XMD cache cleared for new project");
    });

    // Attempt early attach in case DOM is ready before xmd-loaded
    this.attachProjectNameWatcher();

    // Optional: allow other parts of the app to push meta changes
    this.eventHub.on("projectNameChanged", (name) => {
      const el = document.getElementById("toolbar-project-name");
      if (el) el.value = (name || "").trim();
      this.updateCacheMeta({ name });
    });
    this.eventHub.on("projectUuidChanged", (uuid) => {
      this.updateCacheMeta({ uuid });
    });

    // Initial run (won't write cache until xmd-loaded)
    this.updateMakeJson();
  }

  cleanup() {
    if (!this.eventHub) return; // Prevent cleanup if already disposed

    log("Cleaning up XmdTracker...");

    // Unregister event listeners
    this.eventHub.off("exportDesign", this.exportDesignHandler);
    this.eventHub.off("xmd", this.handleSceneChange);
    this.eventHub.off("xmd-loaded");
    this.eventHub.off("newProject");
    this.eventHub.off("projectNameChanged");
    this.eventHub.off("projectUuidChanged");

    // If you attached the watcher, remove it
    if (this._nameWatcherCleanup) {
      this._nameWatcherCleanup();
      this._nameWatcherCleanup = null;
      this._nameWatcherAttached = false;
    }

    log("Unregistered all event listeners");

    // Clear references
    this.experience = null;
    this.scene = null;
    this.eventHub = null;

    log("XmdTracker cleanup completed.");
  }

  /**
   * Handles scene change events by capturing the scene info and updating the JSON.
   */
  handleSceneChange() {
    this.updateMakeJson();
    
    // Hide loading spinner when scene updates (parts loaded successfully)
    // Add a small delay to ensure parts are fully rendered before hiding spinner
    setTimeout(() => {
      log('Part loading complete, hiding spinner');
      this.eventHub.emit("part-loading-end", {
        id: 'current-part-loading', // Use consistent ID
        success: true
      });
    }, 100);
  }

  /**
   * Normalizes the rotation angle of an object to the nearest 90 degrees.
   * @param {THREE.Object3D} object - The object whose angle needs to be normalized.
   * @returns {number} - The normalized angle in degrees.
   */
  getNormalizedAngle(object) {
    const worldQuaternion = new THREE.Quaternion();
    object.getWorldQuaternion(worldQuaternion);
    const worldRotation = new THREE.Euler().setFromQuaternion(
      worldQuaternion,
      "YZX"
    );
    const worldRotationDegrees =
      Math.round(THREE.MathUtils.radToDeg(worldRotation.y) / 90) * 90;
    return (worldRotationDegrees + 360) % 360;
  }

  /**
   * Captures the current state of the scene, including figures and other objects.
   * @returns {Object} - An object representing the current state of the scene.
   */
  captureSceneInfo() {
    const sceneInfo = {
      figures: [],
      others: [],
      connections: [],
      titleBlockData: {},
      tbShowing: false,
      version: "2.0",
    };

    this.scene.traverse((object) => {
      // Check if the object is a figure
      if (object.name === "XMO" && object.userData.mode !== "textbox") {
        const worldPosition = new THREE.Vector3();
        object.getWorldPosition(worldPosition);
        const roundedPosition = {
          x: Math.round(worldPosition.x * 2) / 2,
          y: Math.round(worldPosition.y * 2) / 2,
          z: Math.round(worldPosition.z * 2) / 2,
        };

        let normalizedAngle = this.getNormalizedAngle(object);

        const partData = structuredClone(object.userData);

        sceneInfo.figures.push({
          uuid: object.uuid,
          partData: partData,
          x: roundedPosition.x,
          y: roundedPosition.y,
          z: roundedPosition.z,
          angle: normalizedAngle,
        });
      } else if (object.userData.mode === "textbox") {
        sceneInfo.others.push({
          mode: object.userData.mode,
          text: object.userData.text,
          bold: object.userData.bold,
          outlineColor: object.userData.outlineColor,
          textColor: object.userData.textColor,
          bgColor: object.userData.bgColor,
          fontSize: object.userData.fontSize,
          alpha: object.userData.alpha,
          fontFamily: object.userData.fontFamily,
          x: object.position.x,
          y: object.position.y,
          z: object.position.z,
          w: object.userData.w,
          h: object.userData.h,
          objectId: object.userData.objectId,
          uuid: object.uuid,
          angle: this.getNormalizedAngle(object),
        });
      }
    });

    return sceneInfo;
  }

  /**
   * Updates the makeJson property with the current state of the scene and saves to cache with meta.
   */
  updateMakeJson() {
    if (!this.xmdAlreadyLoaded) {
      log("⏳ Skipping cache save — waiting for xmd-loaded event");
      return;
    }

    const currentSceneInfo = this.captureSceneInfo();
    window.makeJson = currentSceneInfo;
    this.experience.xmdVault = currentSceneInfo;

    // read project name from UI
    const nameEl = document.getElementById("toolbar-project-name");
    const designName =
      nameEl && nameEl.value && nameEl.value.trim()
        ? nameEl.value.trim()
        : "Untitled Design";

    // try to reuse existing UUID (experience/window/cache), else generate one
    const cached = sessionStorage.getItem(CACHE_KEY);
    let cachedUuid = null;
    try {
      cachedUuid = cached ? (JSON.parse(cached)?.meta?.uuid || null) : null;
    } catch {}

    const existingUuid =
      this.experience?.projectUuid ||
      (window && window.currentProjectUuid) ||
      cachedUuid;

    const projectUuid =
      existingUuid ||
      (window.crypto?.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

    // persist in memory for future saves
    this.experience.projectUuid = projectUuid;
    window.currentProjectUuid = projectUuid;

    // wrap and save to sessionStorage
    const payload = {
      xmd: currentSceneInfo,
      meta: {
        name: designName,
        uuid: projectUuid,
        savedAt: new Date().toISOString(),
      },
    };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(payload));
    log("💾 Saved xmdVault (with meta) to sessionStorage");
    log("current state of the xmdVault", currentSceneInfo);

    this.eventHub.emit("xmdUpdated");
    this.eventHub.emit("explosionStop");
  }

  /**
   * Saves the current design by posting a message to the parent window.
   */
  saveDesign() {
    const nameEl = document.getElementById("toolbar-project-name");
    const designName =
      nameEl && nameEl.value && nameEl.value.trim()
        ? nameEl.value.trim()
        : "Untitled Design";

    const data = {
      event: "changedObjects",
      contents: JSON.stringify(window.makeJson),
      name: designName,
      // (optional) include uuid so parent always knows which project this is
      uuid: this.experience?.projectUuid || window.currentProjectUuid,
    };

    log("EVENTHUB EVENT: ", data);
    this.eventHub.emit("changedObjects", data);
  }

  /**
   * Update only the cache metadata (name/uuid) without a scene change.
   * Creates a payload if none exists yet.
   * @param {{name?: string, uuid?: string}} partialMeta
   */
  updateCacheMeta(partialMeta = {}) {
    let payload;
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      payload = cached ? JSON.parse(cached) : null;
    } catch {
      payload = null;
    }

    if (!payload) {
      // No cache yet — build a minimal payload using current XMD if available
      const xmd = window.makeJson || this.captureSceneInfo();
      payload = { xmd, meta: {} };
    }

    payload.meta = payload.meta || {};
    if (typeof partialMeta.name === "string") {
      payload.meta.name = partialMeta.name.trim() || "Untitled Design";
    }
    if (typeof partialMeta.uuid === "string" && partialMeta.uuid) {
      payload.meta.uuid = partialMeta.uuid;
    }
    payload.meta.savedAt = new Date().toISOString();

    sessionStorage.setItem(CACHE_KEY, JSON.stringify(payload));

    // keep in-memory copies in sync
    if (payload.meta.uuid) {
      this.experience.projectUuid = payload.meta.uuid;
      window.currentProjectUuid = payload.meta.uuid;
    }
  }

  /**
   * Watch the toolbar name input and persist changes to cache meta immediately.
   * Safe to call multiple times; it re-tries until the input exists.
   */
  attachProjectNameWatcher() {
    const el = document.getElementById("toolbar-project-name");
    if (!el) {
      setTimeout(() => this.attachProjectNameWatcher(), 300);
      return;
    }
    if (this._nameWatcherAttached) return;
    this._nameWatcherAttached = true;

    const handler = () => {
      const newName = el.value?.trim() || "Untitled Design";
      this.updateCacheMeta({ name: newName });
    };

    el.addEventListener("input", handler);
    el.addEventListener("change", handler);

    this._nameWatcherCleanup = () => {
      el.removeEventListener("input", handler);
      el.removeEventListener("change", handler);
    };
  }
}
